import { useRef } from "react";
import { useController, useFormContext } from "react-hook-form";
import { CountryCode } from "react-native-country-picker-modal";

import { ErrorHelperText } from "../../components";
import PhoneNumberInput, { isValidNumber } from "../../components/PhoneNumberInput";
import { FormData, TextInputProps } from "../AccountScreen/types";

interface Props extends TextInputProps {
  label: string;
  countryCode: CountryCode;
  setCountryCode(v: string): void;
  triggerValidation(): void;
}

function PhoneInput({ name, label, rules, control, countryCode, setCountryCode, testID, triggerValidation }: Props) {
  const {
    field: { onChange: onChangeText, value },
    fieldState: { error },
  } = useController({ control, name, rules });

  const phoneInputRef = useRef<PhoneNumberInput>(null);

  return (
    <>
      <PhoneNumberInput
        placeholder={label}
        ref={phoneInputRef}
        defaultValue={value}
        defaultCode={countryCode}
        onChangeFormattedText={(text) => {
          onChangeText(text);
          setCountryCode(phoneInputRef.current?.getCountryCode() || "");
          triggerValidation();
        }}
        error={error}
        testID={testID}
      />
      {!!error?.message && <ErrorHelperText error={error}>{error?.message}</ErrorHelperText>}
    </>
  );
}

export default function HomeMobileNumberInputs() {
  const { control, getValues, setValue, trigger } = useFormContext<FormData>();
  const { homePhoneCountryCode, mobilePhoneCountryCode } = getValues();
  const setHomeCountryCode = (v: CountryCode) => setValue("homePhoneCountryCode", v, { shouldValidate: false });
  const setMobileCountryCode = (v: CountryCode) => setValue("mobilePhoneCountryCode", v, { shouldValidate: false });

  return (
    <>
      <PhoneInput
        name="homePhone"
        label="Home Phone"
        testID="homePhone"
        control={control}
        countryCode={homePhoneCountryCode}
        setCountryCode={setHomeCountryCode}
        rules={{
          validate: {
            required: (value: string, { mobilePhone }) => {
              if (!mobilePhone && !value) return "At least one phone number is required.";
              if (value && !isValidNumber(value, homePhoneCountryCode)) {
                return "The phone number is not valid.";
              }
              return true;
            },
          },
        }}
        triggerValidation={() => trigger("mobilePhone")}
      />
      <PhoneInput
        name="mobilePhone"
        label="Mobile Phone"
        testID="mobilePhone"
        control={control}
        countryCode={mobilePhoneCountryCode}
        setCountryCode={setMobileCountryCode}
        rules={{
          validate: {
            required: (value: string, { homePhone }) => {
              if (!value && !homePhone) return "At least one phone number is required.";
              if (value && !isValidNumber(value, mobilePhoneCountryCode)) {
                return "The phone number is not valid.";
              }
              return true;
            },
          },
        }}
        triggerValidation={() => trigger("homePhone")}
      />
    </>
  );
}
